// Sucht die korrigierte Rechnung von Hand, wenn die automatische Zuordnung (Sync)
// nicht angeschlagen hat — z. B. weil Lieferant oder Nummer beim Neu-Beleg leicht
// abweichen. Gerüst, Kopf- und Fußzeile wie KorrekturAnfordernModal (Aufgabe 4).
import React, { useState, useEffect } from 'react';
import { Link as LinkIcon, MagnifyingGlass } from '@phosphor-icons/react';
import { DialogHuelle } from '../immobilien/ui/DialogHuelle';
import { useMobilModus } from '../../lib/immobilien/mobil';
import { supabase } from '../../lib/supabase/client';
import { KurzRechnung } from '../../lib/rechnungen/korrekturApi';

interface Props {
  eigeneId: string;
  onClose: () => void;
  onWaehlen: (id: string) => void;
}

const KorrekturVerknuepfenModal: React.FC<Props> = ({ eigeneId, onClose, onWaehlen }) => {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<KurzRechnung[]>([]);
  const { mobil } = useMobilModus();

  useEffect(() => {
    if (suche.trim().length < 2) { setTreffer([]); return; }
    let aktiv = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.from('rechnungen')
        .select('id, rechnungsnummer, datum, brutto')
        .or(`rechnungsnummer.ilike.%${suche.trim()}%,unternehmen.ilike.%${suche.trim()}%`)
        .neq('id', eigeneId)
        // Archivierte scheiden aus: der Vorgang ist dort abgeschlossen, eine archivierte
        // Rechnung ist nie die frisch eingetroffene Korrektur.
        .is('archiviert_am', null)
        .is('korrigiert_durch_id', null)
        .limit(10);
      if (aktiv) setTreffer((data as KurzRechnung[]) || []);
    }, 300);
    return () => { aktiv = false; clearTimeout(t); };
  }, [suche, eigeneId]);

  // Dialog im App-Stil (Rechnungen-Redesign E4); Suche und Regeln unverändert.
  return (
    <DialogHuelle titel="Korrigierte Rechnung zuordnen" icon={LinkIcon} iconFarbe={{ farbe: '#fff', flaeche: '#32ade6' }} onSchliessen={onClose} breite={460}
      fuss={
        <button type="button" onClick={onClose} className="inline-flex flex-1 items-center justify-center"
          style={{ height: 46, borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
          Abbrechen
        </button>
      }>
      <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
        Suche nach Rechnungsnummer oder Lieferant, um die eingetroffene Korrektur zuzuordnen.
      </span>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Suche</span>
        <span className="flex items-center" style={{ height: 46, gap: 8, padding: '0 12px', borderRadius: 14, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <MagnifyingGlass size={15} color="var(--ink3)" className="shrink-0" />
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Rechnungsnummer oder Lieferant" autoFocus
            className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--ink)' }} />
        </span>
      </label>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {treffer.map((r) => (
          <button key={r.id} type="button" onClick={() => onWaehlen(r.id)} className="immo-zeile-hover flex items-center justify-between text-left"
            style={{ minHeight: mobil ? 48 : 42, gap: 8, padding: '8px 12px', borderRadius: 13, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)', background: 'var(--fill)' }}>
            <span className="truncate" style={{ fontSize: 13.5, fontWeight: 800 }}>{r.rechnungsnummer}</span>
            <span className="shrink-0" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>
              {r.datum ? new Date(r.datum).toLocaleDateString('de-DE') : ''}
              {r.brutto != null ? ` · ${r.brutto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : ''}
            </span>
          </button>
        ))}
        {suche.trim().length >= 2 && treffer.length === 0 && (
          <span style={{ padding: '4px 2px', fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>Keine Treffer</span>
        )}
      </div>
    </DialogHuelle>
  );
};

export default KorrekturVerknuepfenModal;
